import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type FormEvent,
} from 'react'

import { deleteLocalMaterial, listLocalMaterials, saveLocalMaterial } from '../lib/materialStore'
import {
  MATERIAL_TYPES,
  type KnowledgeItem,
  type LocalMaterialRecord,
  type MaterialSummary,
  type MaterialType,
  type VaultConnectionStatus,
  type VaultWriteResult,
} from '../types'
import { Icon } from './Icon'

const MAX_FILE_SIZE = 10 * 1024 * 1024
const ACCEPTED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp'])

function formatFileSize(bytes: number) {
  if (bytes < 1024 * 1024) {
    return `${Math.max(1, Math.round(bytes / 1024))} KB`
  }

  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

function StoredMaterialRow({
  record,
  onDelete,
}: {
  record: LocalMaterialRecord
  onDelete: (id: string) => void
}) {
  const [previewUrl, setPreviewUrl] = useState('')

  useEffect(() => {
    const nextUrl = URL.createObjectURL(record.file_blob)
    setPreviewUrl(nextUrl)
    return () => URL.revokeObjectURL(nextUrl)
  }, [record.file_blob])

  return (
    <article className="material-row">
      <div className="material-row__thumb">
        {previewUrl ? <img alt="" src={previewUrl} /> : <Icon name="image" />}
      </div>
      <div className="material-row__copy">
        <strong title={record.title}>{record.title}</strong>
        <span>{record.material_type} · {formatFileSize(record.file_size)}</span>
      </div>
      <span className="status-chip status-chip--success">
        {record.vault_note_path ? '已写入 Obsidian' : '本机已存'}
      </span>
      <button
        aria-label={`删除 ${record.title}`}
        className="icon-button"
        onClick={() => onDelete(record.id)}
        type="button"
      >
        <Icon name="trash" size={16} />
      </button>
    </article>
  )
}

function SyncedMaterialRow({ record }: { record: MaterialSummary }) {
  return (
    <article className="material-row material-row--synced">
      <div className="material-row__thumb"><Icon name="file" /></div>
      <div className="material-row__copy">
        <strong title={record.title}>{record.title}</strong>
        <span>{record.materialType} · {record.date}</span>
      </div>
      <span className="status-chip status-chip--success">{record.status}</span>
    </article>
  )
}

type MaterialUploadPanelProps = {
  knowledge: KnowledgeItem[]
  materials: MaterialSummary[]
  obsidianMessage: string
  obsidianStatus: VaultConnectionStatus
  onConnectObsidian: () => Promise<boolean>
  onCountChange: (count: number) => void
  onWriteToObsidian: (record: LocalMaterialRecord) => Promise<VaultWriteResult | null>
  studentId: string
}

export function MaterialUploadPanel({
  knowledge,
  materials,
  obsidianMessage,
  obsidianStatus,
  onConnectObsidian,
  onCountChange,
  onWriteToObsidian,
  studentId,
}: MaterialUploadPanelProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [records, setRecords] = useState<LocalMaterialRecord[]>([])
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState('')
  const [materialType, setMaterialType] = useState<MaterialType>('待分类')
  const [knowledgeId, setKnowledgeId] = useState('')
  const [notes, setNotes] = useState('')
  const [statusMessage, setStatusMessage] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [isDragging, setIsDragging] = useState(false)

  useEffect(() => {
    let isActive = true

    listLocalMaterials()
      .then((nextRecords) => {
        if (isActive) {
          setRecords(nextRecords)
          onCountChange(nextRecords.length)
        }
      })
      .catch(() => {
        if (isActive) {
          setStatusMessage('当前浏览器无法读取本地图片资料。')
        }
      })

    return () => {
      isActive = false
    }
  }, [onCountChange])

  useEffect(() => {
    if (!pendingFile) {
      setPreviewUrl('')
      return undefined
    }

    const nextUrl = URL.createObjectURL(pendingFile)
    setPreviewUrl(nextUrl)
    return () => URL.revokeObjectURL(nextUrl)
  }, [pendingFile])

  function selectFile(file: File | undefined) {
    setStatusMessage('')

    if (!file) {
      return
    }

    if (!ACCEPTED_TYPES.has(file.type)) {
      setStatusMessage('仅支持 JPG、PNG 或 WebP 图片。')
      return
    }

    if (file.size > MAX_FILE_SIZE) {
      setStatusMessage('单张图片不能超过 10 MB。')
      return
    }

    setPendingFile(file)
  }

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    selectFile(event.target.files?.[0])
    event.target.value = ''
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault()
    setIsDragging(false)
    selectFile(event.dataTransfer.files[0])
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (!pendingFile || isSaving) {
      setStatusMessage('请先选择一张图片。')
      return
    }

    setIsSaving(true)
    const now = new Date().toISOString()
    const id = `material-${crypto.randomUUID()}`
    const record: LocalMaterialRecord = {
      id,
      student_id: studentId,
      title: pendingFile.name,
      subject: '数学',
      grade: '高一',
      material_type: materialType,
      source_scene: '网页本地上传',
      material_date: now.slice(0, 10),
      original_file_name: pendingFile.name,
      mime_type: pendingFile.type,
      file_size: pendingFile.size,
      related_knowledge_ids: knowledgeId ? [knowledgeId] : [],
      processing_status: '待处理',
      content_status: 'raw',
      review_level: 'L0',
      audit_status: '待审核',
      copyright_status: '家庭合法拥有，待核验',
      storage_scope: 'browser_local',
      storage_key: id,
      notes: notes.trim(),
      created_at: now,
      updated_at: now,
      file_blob: pendingFile,
    }

    try {
      await saveLocalMaterial(record)
      let storedRecord = record
      let nextStatusMessage = '图片已保存到当前浏览器，本地资料队列已更新。'

      if (obsidianStatus === 'connected') {
        try {
          const vaultResult = await onWriteToObsidian(record)

          if (vaultResult) {
            storedRecord = {
              ...record,
              vault_asset_path: vaultResult.assetPath,
              vault_note_path: vaultResult.notePath,
            }
            await saveLocalMaterial(storedRecord)
            nextStatusMessage = '图片和 Markdown 已写入 Obsidian，同时保留浏览器本机副本。'
          }
        } catch {
          nextStatusMessage = '图片已保存到浏览器，但 Obsidian 写入失败，请重新授权后再试。'
        }
      }

      setRecords((current) => {
        const nextRecords = [storedRecord, ...current]
        onCountChange(nextRecords.length)
        return nextRecords
      })
      setPendingFile(null)
      setMaterialType('待分类')
      setKnowledgeId('')
      setNotes('')
      setStatusMessage(nextStatusMessage)
    } catch {
      setStatusMessage('图片保存失败，请检查浏览器存储权限或剩余空间。')
    } finally {
      setIsSaving(false)
    }
  }

  async function handleDelete(id: string) {
    try {
      await deleteLocalMaterial(id)
      setRecords((current) => {
        const nextRecords = current.filter((record) => record.id !== id)
        onCountChange(nextRecords.length)
        return nextRecords
      })
      setStatusMessage('本机图片记录已删除。')
    } catch {
      setStatusMessage('删除失败，请稍后再试。')
    }
  }

  return (
    <section aria-labelledby="material-upload-heading" className="panel-frame material-panel" id="material-upload">
      <header className="panel-header material-panel__header">
        <div className="panel-title">
          <span className="panel-title__icon"><Icon name="image" /></span>
          <div>
            <h2 id="material-upload-heading">本地图片资料录入</h2>
          </div>
        </div>
        <button className="panel-action-button" onClick={() => inputRef.current?.click()} type="button">
          添加图片资料 <Icon name="upload" size={15} />
        </button>
      </header>

      <form className="upload-form" onSubmit={handleSubmit}>
        <input
          accept="image/jpeg,image/png,image/webp"
          className="visually-hidden"
          id="material-file-input"
          onChange={handleFileChange}
          ref={inputRef}
          type="file"
        />

        <div
          className={`upload-zone${isDragging ? ' upload-zone--dragging' : ''}${pendingFile ? ' upload-zone--selected' : ''}`}
          onDragEnter={() => setIsDragging(true)}
          onDragLeave={() => setIsDragging(false)}
          onDragOver={(event) => event.preventDefault()}
          onDrop={handleDrop}
        >
          {pendingFile && previewUrl ? (
            <div className="pending-preview">
              <img alt={`待保存图片：${pendingFile.name}`} src={previewUrl} />
              <div>
                <strong>{pendingFile.name}</strong>
                <span>{formatFileSize(pendingFile.size)}</span>
              </div>
              <button className="text-button" onClick={() => setPendingFile(null)} type="button">
                更换
              </button>
            </div>
          ) : (
            <button className="upload-zone__button" onClick={() => inputRef.current?.click()} type="button">
              <span className="upload-zone__icon"><Icon name="upload" size={25} /></span>
              <strong>拖入图片，或点击选择</strong>
              <small>JPG / PNG / WebP · 单张不超过 10 MB</small>
            </button>
          )}
        </div>

        {pendingFile ? (
          <div className="upload-fields">
            <label>
              <span>资料类型</span>
              <select value={materialType} onChange={(event) => setMaterialType(event.target.value as MaterialType)}>
                {MATERIAL_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
              </select>
            </label>
            <label>
              <span>关联知识模块</span>
              <select value={knowledgeId} onChange={(event) => setKnowledgeId(event.target.value)}>
                <option value="">暂不关联</option>
                {knowledge.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </select>
            </label>
            <label className="upload-fields__notes">
              <span>备注</span>
              <input
                maxLength={120}
                onChange={(event) => setNotes(event.target.value)}
                placeholder="例如：学校课堂例题第 3 页"
                value={notes}
              />
            </label>
            <button className="primary-button" disabled={isSaving} type="submit">
              <Icon name="database" size={17} />
              {isSaving ? '正在保存…' : '保存到本机'}
            </button>
          </div>
        ) : null}
      </form>

      <div className={`vault-connection-bar vault-connection-bar--${obsidianStatus}`}>
        <div>
          <Icon name={obsidianStatus === 'connected' ? 'check' : 'link'} size={15} />
          <span>{obsidianMessage}</span>
        </div>
        {obsidianStatus !== 'connected' && obsidianStatus !== 'unsupported' ? (
          <button onClick={() => void onConnectObsidian()} type="button">
            {obsidianStatus === 'permission-needed' ? '重新授权' : '连接知识库'}
          </button>
        ) : null}
      </div>

      <p aria-live="polite" className="upload-status">
        {statusMessage || (obsidianStatus === 'connected'
          ? '保存时会同时生成 Obsidian 图片附件和待整理 Markdown 笔记。'
          : '未连接 Obsidian 时，图片只保存在当前浏览器。')}
      </p>

      <div className="material-list-heading">
        <strong>最近资料（本地保存）</strong>
        <span>{obsidianStatus === 'connected' ? 'Obsidian + IndexedDB' : 'IndexedDB 本机存储'}</span>
      </div>
      <div className="material-list">
        {records.length > 0 || materials.length > 0 ? (
          <>
            {records.slice(0, 3).map((record) => (
            <StoredMaterialRow key={record.id} onDelete={handleDelete} record={record} />
            ))}
            {materials.slice(0, Math.max(0, 3 - records.length)).map((record) => (
              <SyncedMaterialRow key={record.id} record={record} />
            ))}
          </>
        ) : (
          <div className="compact-empty">
            <Icon name="image" size={22} />
            <span>尚无本机图片资料</span>
          </div>
        )}
      </div>
    </section>
  )
}
